import { Component, inject, OnInit, signal } from '@angular/core';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular/standalone';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import {
  WorkCreateTaskDto,
  WorkDocumentsV2Service,
  WorkProject,
  WorkProjectsV2Service,
  WorkTasksV2Service,
} from '@work-frontend-monorepo/work-rest-api';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { AlertDialogService } from '@work-frontend-monorepo/work-ui/modals';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { InputFieldComponent } from '@work-frontend-monorepo/work-ui/forms';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';

interface CapturedPhoto {
  dataUrl: string;
  blob: Blob;
  fileName: string;
}

@Component({
  selector: 'sfg-app-projekte-details-schaden-melden-page',
  templateUrl: './projekte-details-schaden-melden-page.component.html',
  styleUrls: ['./projekte-details-schaden-melden-page.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    IonBackButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonMenuButton,
    IonTitle,
    IonToolbar,
    InputFieldComponent,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class ProjekteDetailsSchadenMeldenPageComponent implements OnInit {
  project = signal<WorkProject | undefined>(undefined);
  capturedPhotos = signal<CapturedPhoto[]>([]);
  submitLoadingState = signal<LoadingAndFeedbackState>('none');
  notesControl = new FormControl<string>('');

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly navController = inject(NavController);
  private readonly workProjectsV2Service = inject(WorkProjectsV2Service);
  private readonly workDocumentsV2Service = inject(WorkDocumentsV2Service);
  private readonly workTasksV2Service = inject(WorkTasksV2Service);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);

  async ngOnInit() {
    const projectId = this.activatedRoute.snapshot.params['projectId'];
    if (projectId) {
      await this.loadProjectDetails(parseInt(projectId, 10));
    }
  }

  protected async takePhoto() {
    await this.pickPhoto(CameraSource.Camera);
  }

  protected async pickFromGallery() {
    await this.pickPhoto(CameraSource.Photos);
  }

  protected removePhoto(index: number) {
    this.capturedPhotos.update((photos) => photos.filter((_, i) => i !== index));
  }

  protected async submit() {
    if (this.submitLoadingState() === 'loading') return;

    this.submitLoadingState.set('loading');
    try {
      const project = this.project();
      const projectId = project?.id;
      const kundenberaterId = project?.userId ?? undefined;

      // 1. Fotos hochladen (documenttypeId: 2, documentsubtypeId: 13 => Projektfoto)
      for (const photo of this.capturedPhotos()) {
        await lastValueFrom(
          this.workDocumentsV2Service.documentsV2ControllerUploadFile({
            body: {
              file: new File([photo.blob], photo.fileName, { type: 'image/jpeg' }),
              prettyName: photo.fileName,
              documenttypeId: 2,
              documentsubtypeId: 13,
              projectId: projectId ?? null,
            },
          }),
        );
      }

      // 2. Task erstellen und dem Kundenberater zuweisen
      const notes = this.notesControl.value ?? '';
      const createTaskDto: WorkCreateTaskDto = {
        active: true,
        mandatory: true,
        tasktypeId: 1,
        name: 'Schadenfall bearbeiten',
        projectId: projectId ?? null,
        userId: kundenberaterId ?? null,
        notes: notes || null,
      };

      await lastValueFrom(
        this.workTasksV2Service.tasksV2ControllerCreate({
          body: createTaskDto,
        }),
      );

      this.submitLoadingState.set('success');
      this.alertDialogService.showAlert({
        title: 'SCHADEN_MELDEN_ERFOLGREICH',
        feedbackState: 'success',
      });

      // Zurück zur Projektdetailseite navigieren
      await this.navController.navigateBack(['/mitarbeitende/projekte/details', projectId], {
        animated: true,
        queryParams: { ignoreErstinstruktion: true },
      });
    } catch (submitException) {
      this.submitLoadingState.set('error');
      this.exceptionHandlingService.handleExceptionWithAlert('schadenMeldenException', submitException);
    }
  }

  private async loadProjectDetails(projectId: number) {
    try {
      const projectResponse = await lastValueFrom(
        this.workProjectsV2Service.projectsV2ControllerFindOneById({
          id: projectId,
          withAllDetails: true,
        }),
      );
      this.project.set(projectResponse);
    } catch (loadProjectException) {
      this.exceptionHandlingService.handleExceptionWithAlert('loadProjectException', loadProjectException);
    }
  }

  private async pickPhoto(source: CameraSource) {
    try {
      const photo: Photo = await Camera.getPhoto({
        quality: 80,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source,
        saveToGallery: false,
      });

      if (!photo.dataUrl) return;

      const blob = await this.dataUrlToBlob(photo.dataUrl);
      const fileName = `schaden_${Date.now()}.jpg`;

      this.capturedPhotos.update((photos) => [...photos, { dataUrl: photo.dataUrl!, blob, fileName }]);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (!message?.toLowerCase().includes('cancel') && !message?.toLowerCase().includes('dismissed')) {
        this.exceptionHandlingService.handleExceptionWithAlert('takePhotoException', error);
      }
    }
  }

  private async dataUrlToBlob(dataUrl: string): Promise<Blob> {
    const response = await fetch(dataUrl);
    return response.blob();
  }
}
